<template>
	<li v-if="listing.isSpacer != null" class="listing-item--full ml-5 -mr-5">
		<div class="relative listings-border-title flex justify-center mt-50 mb-55">
			<h3 class="bg-gray50 px-30 text-gray500">All Listings</h3>
		</div>
	</li>

	<li
		v-else
		class="listing-item"
		:class="[stacked ? 'stacked' : '' ]"
		:disabled="disabled || null"
		:style="{ display: disabled ? 'none' : '' }"
		data-listing-item
	>
		<a 	class="listing-item-header cursor-pointer"
			:class="{ 'w-1/2 flex-no-shrink -my-10 rounded-r': stacked }"
			@click="openListing"
			:href="listingUrl"
		>
			<span
				:href="listingUrl"
				class="bg-blue200 cursor-pointer absolute pin bg-cover bg-center"
				v-lazy:background-image="backgroundImage"
			></span>
		</a>
		<div class="listing-item-details-wrapper">
			<a 	class="listing-item-details cursor-pointer"
				:class="{ '-ml-30': stacked }"
				@click="openListing"
			>

				<div
					class="listing-item-type"
					:class="{ 'listing-item-type-stacked': stacked }"
				>
					<div class="absolute pin opacity-40 rounded-b" :class="[{ 'rounded-bl-none': stacked }, listingColor.replace('text-', 'bg-') ]"></div>
					<div class="relative">
						{{ propertyLabel }}
					</div>
				</div>

				<div class="listing-item-details-top">
					<div class="flex items-center" :class="listing.property.type">
						<ListingIcon :type="listing.property.type" :sub-type="listing.property.subType" />
						<div class="ml-15 text-xl text-gray700 font-secondary font-bold">${{ price || "Unlisted" }}</div>
					</div>

					<div class="mt-20">
						<div class="font-bold text-gray700">{{ street }}</div>
						<div class="mt-10 text-sm">{{ cityStateZip }}</div>
					</div>

				</div>
				<div class="border-t border-solid border-gray100">
					<div class="-mx-10 p-30 flex justify-around">
						<div class="flex px-10" v-if="beds">
							<span class="icon-m-bed text-gray300"></span>
							<div class="listing-info-item ml-10">{{ beds }} <span class="label">b<span class="longhand">e</span>d<span class="longhand">s</span></span></div>
						</div>

						<div class="flex px-10" v-if="baths">
							<span class="icon-m-tub text-gray300"></span>
							<div class="listing-info-item ml-10">{{ baths }} <span class="label">ba<span class="longhand">ths</span></span></div>
						</div>

						<div class="flex px-10" v-if="area">
							<span class="icon-m-measure text-gray300"></span>
							<div class="listing-info-item ml-10">{{ area }}</div>
						</div>

						<div class="flex px-10 acres" v-if="listing.property.type == 'LND' && acres">
							<span class="icon-m-tree text-gray300"></span>
							<div class="listing-info-item ml-10">{{ acres }}</div>
						</div>
					</div>
				</div>
			</a>
            <div class="mt-15 text-sm italic text-center" v-if="showRepresentationLabel && parentAgentId">
              <span v-if="agentRepresenting">
                Represented {{ agentRepresenting != 'seller' ? 'Buyer' : 'Seller' }}
              </span>
              <span v-else>
                Represented {{ listing.agent.id != parentAgentId ? 'Buyer' : 'Seller'}}
              </span>
            </div>
		</div>
	</li>
</template>

<script>
// import Vue components
import ListingIcon from './listing-icon.vue';

// import helpers
import stateToAbbreviation from '../../helpers/stateToAbbr.js';
import formatPrice from '../../helpers/formatPrice.js';
import addCommas from '../../helpers/addCommas.js';
import lazyBackground from '../../directives/lazy-background';

// Residential listing card used by search, community, sold, and agent pages.
export default {
	directives: {
		lazy: lazyBackground,
	},
	props: {
		listing: { type: Object, required: true },
		orientation: { type: String, default: 'horizontal' },
		parentAgentId: { type: [String, Number], default: null },
		agentRepresenting: { type: String, default: '' },
		// Representation labels are opt-in so active listings do not inherit Sold-tab context by accident.
		showRepresentationLabel: { type: Boolean, default: false },
		splitActive: { type: Boolean, default: true },
	},
	data() {
		return {
			types: {
				'townhouse': {
					defaultColor: 'text-red500',
					icon: 'townhouse',
				},
				'condominium': {
					defaultColor: 'text-purple500',
					icon: 'condo',
				},
				'apartment': {
					defaultColor: 'text-red500',
					icon: 'townhouse',
				},
				'RES': {
					defaultColor: 'text-red500',
					icon: 'single-family',
				},
				'RNT': {
					defaultColor: 'text-red500',
					icon: 'single-family',
				},
				'MLF': {
					defaultColor: 'text-blue500',
					icon: 'multifamily',
				},
				'CRE': {
					defaultColor: 'text-gray500',
					icon: 'commercial',
				},
				'LND': {
					defaultColor: 'text-green500',
					icon: 'land',
				},
			}
		}
	},
	computed: {
		stacked() {
			return this.orientation == 'vertical';
		},
		disabled() {
			return this.stacked && !this.splitActive;
		},
		listingColor() {
			var typeAbbreviation = this.listing.property.type
			var subType = String(this.listing.property.subType).toLowerCase()

			var type = this.types[subType] || this.types[typeAbbreviation]

			return type.defaultColor
		},
		backgroundImage() {
			if (
				!this.listing ||
				!this.listing.photos ||
				this.listing.photos.length === 0
			) {
				return null;
			}

			const bgImage = this.listing.photos[0];

			return bgImage
		},
		street() {
			const address = this.listing.address;
			return address.full;
		},
		stateAbbr() {
			const state = this.listing.address.state;
			return stateToAbbreviation(state) || state;
		},
		cityStateZip() {
			const address = this.listing.address;
			const { state, city, postalCode: zip } = address;

			return `${city}, ${this.stateAbbr} ${zip}`;
		},
		listingUrl() {
			const urlId = encodeURIComponent(this.listing.listingId);

			return window.residentialUrl + `/listings/${urlId}`;
		},
		price() {
			//check status? this.listing.mls.status -> closePrice is null if not closed (might not need to check)


			let price = this.listing.sales && this.listing.sales.closePrice ? this.listing.sales.closePrice : this.listing.listPrice

			return formatPrice(price);
		},
		propertyLabel() {

			var propertyLabel = ''

			let type = this.listing.property.type
			let subType = String(this.listing.property.subType).toLowerCase()
			let status = String(this.listing.mls.status).toLowerCase()

			// Determine base label
			if (subType == 'townhouse') {
				propertyLabel += 'Townhouse'
			} else if (subType == 'condominium') {
				propertyLabel += 'Condo'
			} else if (subType == 'apartment') {
				propertyLabel += 'Apartment'
			} else if (type == 'RES') {
				propertyLabel += 'Single Family'
			} else if (type == 'MLF') {
				propertyLabel += 'Multi-Family'
			} else if (type == 'RNT') {
				propertyLabel += 'Condo'
			} else if (type == 'LND') {
				propertyLabel += 'Land'
			}

			// Add Status if necessary
			if (status == 'pending') {
				propertyLabel += ' • Pending'
			} else if (status == 'activeundercontract') {
				propertyLabel += ' • Under Contract'
			}
			else if(status == 'comingsoon'){
				propertyLabel += ' • Coming Soon'
			}
			else if(status == 'closed'){
				propertyLabel += ' • Closed'
			}

			return propertyLabel
		},
		beds() {
			return this.listing.property.bedrooms
		},
		baths() {
		    let properties = this.listing.property;
			return properties.bathsFull + properties.bathsHalf + properties.bathsThreeQuarter;
		},
		area() {

			let area = this.listing.property.area
			let areaWithCommas = addCommas(area) + " sqft"

			if(area) {
				return areaWithCommas
			}
		},
		acres() {
			let acres = this.listing.property.acres
			let acresWithCommas = addCommas(acres) + " acres"

			if(acres) {
				return acresWithCommas
			}
		},
		type() {
			let type = this.listing.property.subType
			type = this.convertCamel(type)
			return type
		},
	},
	methods: {
		openListing() {
			var initialHash = window.location.search.split('mlsid')[0]
			window.location.search = initialHash + 'mlsid=' + this.listing.mlsId
			window.location.href = this.listingUrl
		},
		convertCamel(string) {
			try {
			    string = string.replace(/([A-Z])/g, ' $1')
			    string = string.replace(/^./, function(str){ return str.toUpperCase(); })
			} catch(err) {
				console.error(err)
			}
			return string
		}
	},
	components: {
		ListingIcon
	},
};
</script>
